import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import classnames from "classnames";
import { parse } from "qs";

import { DialogBox, DialogBoxFooterType, Loader, LoaderTypes, SubSection } from "~/core";
import { messages } from "~/i18n-messages";
import { ThemeRoot } from "~/theme";
import { APIErrorWithCode, ReportAPI } from "@ai360/core";
import { Section } from "../../core";

import * as actions from "../actions";
import * as selectors from "../selectors";
import "./login.css";
import { getErrorMessages } from "~/i18n-error-messages";
import { logFirebaseEvent } from "~/utils/firebase";

export interface IMyAgDataGrowerReview_Props {
    error?: Error;
    location?: Record<string, any>;
    onStartProcessing: () => void;
    onStopProcessing: () => void;
    initMyAgData: () => void;
    isProcessing?: boolean;
    intl: intlShape;
}

const MyAgDataGrowerReview_ = (props: IMyAgDataGrowerReview_Props): JSX.Element => {
    const { location, onStartProcessing, onStopProcessing, isProcessing, intl } = props;
    const { formatMessage } = intl;
    const { request } = parse(location.search.slice(1));

    const [info, setInfo] = useState<ReportAPI.IMyAgDataReviewResponse>(null);
    const [shaking, setShaking] = useState<boolean>(false);
    const [stage, setStage] = useState<
        "need-approval" | "pending" | "done" | "errored" | "not-found"
    >("need-approval");
    const [message, setMessage] = useState<string | string[] | null>(null);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    useEffect(() => {
        props.initMyAgData();
    }, []);

    useEffect(() => {
        if (request) {
            onStartProcessing();
            ReportAPI.getMyAgDataReview(request)
                .then((response) => {
                    setInfo(response);
                    onStopProcessing();
                    if (response == null || !response.approvalReady) {
                        window.open("/");
                        setStage("done");
                    } else if (response.alreadyApproved) {
                        setStage("done");
                        setMessage(null);
                    } else {
                        setStage("need-approval");
                    }
                })
                .catch((err) => {
                    _handleError(err);
                })
                .finally(() => console.log("fetch ended"));
        }
    }, [request]);

    const errMsgEl = useMemo(
        () =>
            Array.isArray(message) ? message.map((msg, idx) => <p key={idx}>{msg}</p>) : message,
        [message]
    );

    function _handleError(err) {
        let errMessage: string;
        if (err instanceof APIErrorWithCode) {
            errMessage = getErrorMessages(formatMessage, err);
        } else if (err?.apiResultObj?.status === 404) {
            setStage("not-found");
        } else {
            errMessage = formatMessage(messages.unknownError);
        }
        setShaking(true);
        setMessage(errMessage);

        onStopProcessing();
    }

    function _accept() {
        setStage("pending");
        setShowConfirm(false);
        logFirebaseEvent("myagdata_approved");
        ReportAPI.submitMyAgData(request)
            .then((result) => {
                if (!result) {
                    setMessage("MyAgData rejected approval request.");
                    setStage("errored");
                } else {
                    setStage(result.success ? "done" : "errored");
                    if (
                        result.message?.trim().toLowerCase() ===
                        "your report been submitted successfully."
                    ) {
                        setMessage(formatMessage(messages.myAgDataIsApprovalSuccess));
                    } else {
                        setMessage(result.message);
                    }
                }
            })
            .catch((err) => {
                _handleError(err);
                setStage("done");
            });
    }

    function renderCanApprove() {
        return (
            <React.Fragment>
                <div className="validation-summary-errors">{errMsgEl}</div>
                <p>{formatMessage(messages.myAgDataApproveText, info)}</p>
                <a href={info.reviewPdfUrl} target="_blank" rel="noreferrer">
                    {formatMessage(messages.myAgDataApproveLink, info)}
                </a>
                <button className="default-button" onClick={() => _accept()}>
                    {formatMessage(messages.myAgDataApprove)}
                </button>

                <DialogBox
                    footerType={DialogBoxFooterType.YES_NO}
                    isOpen={showConfirm}
                    onAction={() => _accept()}
                    onClose={() => setShowConfirm(false)}
                    title={formatMessage(messages.confirmTitle)}
                >
                    {formatMessage(messages.myAgDataConfirmation)}
                </DialogBox>
            </React.Fragment>
        );
    }

    function renderPending() {
        return (
            <React.Fragment>
                <p>{formatMessage(messages.myAgDataApprovePending)}</p>
                <div>
                    <Loader
                        className={"inline-loader"}
                        type={LoaderTypes.LINE_SCALE_PULSE_OUT_RAPID}
                    />
                </div>
                <a href="#" onClick={() => window.close()}>
                    {formatMessage(messages.myAgDataRegistrationCloseWindow)}
                </a>
            </React.Fragment>
        );
    }

    function renderDone() {
        return (
            <React.Fragment>
                <p>{stage !== "done" && formatMessage(messages.myAgDataIsApprovalFail)}</p>
                <div className={stage !== "done" ? "validation-summary-errors" : ""}>
                    {errMsgEl}
                </div>
                <p>{formatMessage(messages.myAgDataRegistrationClosePrompt)}</p>
                <a href="#" onClick={() => window.close()}>
                    {formatMessage(messages.myAgDataRegistrationCloseWindow)}
                </a>
            </React.Fragment>
        );
    }

    function renderNotFound() {
        return (
            <React.Fragment>
                <p>{formatMessage(messages.myAgDataReviewNotFound)}</p>
                <a href="#" onClick={() => window.close()}>
                    {formatMessage(messages.myAgDataRegistrationCloseWindow)}
                </a>
            </React.Fragment>
        );
    }

    const loginBoxClasses = [
        "login-box",
        {
            "login-err-animation": shaking,
        },
    ];

    return (
        <ThemeRoot className="login-root">
            {isProcessing ? <Loader /> : null}
            {!info && stage !== "not-found" ? (
                <Loader />
            ) : (
                <div className="login-page-container">
                    <div className={classnames(loginBoxClasses)}>
                        <div className="login-logo-div"></div>
                        <div className={"myagdata-container"}>
                            <div className="display-label">
                                {formatMessage(messages.myAgDataTitle)}
                            </div>
                            <Section>
                                <SubSection className="myagdata-registration">
                                    {stage === "need-approval" && renderCanApprove()}
                                    {stage === "pending" && renderPending()}
                                    {(stage === "done" || stage == "errored") && renderDone()}
                                    {stage === "not-found" && renderNotFound()}
                                </SubSection>
                            </Section>
                        </div>
                    </div>
                </div>
            )}
        </ThemeRoot>
    );
};

const mapStateToProps = (state): Partial<IMyAgDataGrowerReview_Props> => {
    return {
        error: selectors.getError(state),
        isProcessing: selectors.isProcessing(state),
    };
};

const mapDispatchToProps = (dispatch): Partial<IMyAgDataGrowerReview_Props> => {
    return {
        onStartProcessing: () => dispatch(actions.setProcessing(true)),
        onStopProcessing: () => dispatch(actions.setProcessing(false)),
        initMyAgData: () => dispatch(actions.initialMyAgDataLoad("MyAgData Acreage Reporting")),
    };
};

export const MyAgDataGrowerReview = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(MyAgDataGrowerReview_));
